import React, { useEffect } from "react";
import { BrowserRouter as Router, Route, Routes, Navigate, useNavigate } from "react-router-dom";
import Login from "./components/Login";
import Signup from "./components/Signup";
import Dashboards from "./components/Dashboards";
import DashboardBuilder from "./components/DashboardBuilder";
import DashboardViewer from "./components/DashboardViewer";
import LogExplorer from "./components/LogExplorer";
import ChangePassword from "./components/ChangePassword";
import Alerts from "./components/Alerts";
import PasswordReset from "./components/PasswordReset";
import VerifyEmail from "./components/VerifyEmail";
import AcceptInvite from "./components/AcceptInvite";
import BillingSuccess from "./components/BillingSuccess";
import BillingCancel from "./components/BillingCancel";
import SettingsLayout from "./components/settings/SettingsLayout";
import SettingsProfile from "./components/settings/SettingsProfile";
import SettingsOrganization from "./components/settings/SettingsOrganization";
import SettingsBilling from "./components/settings/SettingsBilling";
import SettingsApiKeys from "./components/settings/SettingsApiKeys";
import SettingsSecurity from "./components/settings/SettingsSecurity";
import "./userTimezoneSettings.css";
import "./print.css";
import { setupActivityListeners } from "./utils/sessionManager";
import { goToLogin } from "./utils/authRedirect";
import { AuthProvider, useAuth } from "./context/AuthContext";

function ProtectedRoute({ isAuthenticated, children }) {
  if (isAuthenticated) return children;
  return <Navigate to="/login" replace />;
}

function UnauthenticatedOnlyRoute({ isAuthenticated, children }) {
  if (isAuthenticated) return <Navigate to="/dashboards" replace />;
  return children;
}

function AppContent() {
  const {
    isAuthenticated,
    isLoading,
    bootstrapSession,
    logout,
  } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    bootstrapSession();

    const cleanup = setupActivityListeners(() => {
      console.log("Session expired due to inactivity - logging out");
      logout().finally(() => {
        goToLogin(navigate);
      });
    });

    return () => {
      if (cleanup) cleanup();
    };
  }, [bootstrapSession, logout, navigate]);

  if (isLoading) {
    return (
      <div style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
        fontSize: "18px",
      }}>
        Loading...
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/" element={isAuthenticated ? <Navigate to="/dashboards" replace /> : <Navigate to="/login" replace />} />
      <Route path="/login" element={
        <UnauthenticatedOnlyRoute isAuthenticated={isAuthenticated}>
          <Login onLoginSuccess={() => {}} />
        </UnauthenticatedOnlyRoute>
      } />
      <Route path="/signup" element={
        <UnauthenticatedOnlyRoute isAuthenticated={isAuthenticated}>
          <Signup />
        </UnauthenticatedOnlyRoute>
      } />
      <Route path="/password-reset" element={<PasswordReset />} />
      <Route path="/reset-password" element={<PasswordReset />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/accept-invite" element={<AcceptInvite />} />

      <Route path="/dashboard" element={<Navigate to="/dashboards" replace />} />
      <Route path="/dashboard/*" element={<Navigate to="/login" replace />} />

      <Route path="/dashboards" element={<ProtectedRoute isAuthenticated={isAuthenticated}><Dashboards /></ProtectedRoute>} />
      <Route path="/dashboards/:dashboardName/dashboard-builder" element={<ProtectedRoute isAuthenticated={isAuthenticated}><DashboardBuilder /></ProtectedRoute>} />
      <Route path="/dashboards/:dashboardName/dashboard-viewer" element={<ProtectedRoute isAuthenticated={isAuthenticated}><DashboardViewer /></ProtectedRoute>} />

      <Route path="/dashboard-builder" element={<ProtectedRoute isAuthenticated={isAuthenticated}><DashboardBuilder /></ProtectedRoute>} />

      <Route path="/logs" element={<ProtectedRoute isAuthenticated={isAuthenticated}><LogExplorer /></ProtectedRoute>} />
      <Route path="/alerts" element={<ProtectedRoute isAuthenticated={isAuthenticated}><Alerts /></ProtectedRoute>} />
      <Route path="/change-password" element={<ProtectedRoute isAuthenticated={isAuthenticated}><ChangePassword /></ProtectedRoute>} />
      <Route path="/account-profile" element={<Navigate to="/settings/profile" replace />} />
      <Route path="/settings" element={<ProtectedRoute isAuthenticated={isAuthenticated}><SettingsLayout /></ProtectedRoute>}>
        <Route index element={<Navigate to="profile" replace />} />
        <Route path="profile" element={<SettingsProfile />} />
        <Route path="organization" element={<SettingsOrganization />} />
        <Route path="billing" element={<SettingsBilling />} />
        <Route path="api-keys" element={<SettingsApiKeys />} />
        <Route path="security" element={<SettingsSecurity />} />
      </Route>
      <Route path="/billing/success" element={<BillingSuccess />} />
      <Route path="/billing/cancel" element={<BillingCancel />} />
      <Route path="*" element={<Navigate to={isAuthenticated ? "/dashboards" : "/login"} replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <AppContent />
      </Router>
    </AuthProvider>
  );
}

export default App;
